import { useReactiveVar } from '@apollo/client';

import { userStateVar } from 'core/state/userState';
import { UserRole } from 'types';

/**
 * Capability booleans for the Teacher card, derived from the current user's
 * roles. Mirrors `useStudentPermissions`. Hiding a control is a UX affordance
 * only — the backend remains the authority on each mutation.
 */
export interface ITeacherPermissions {
  /** Edit the teacher record + append applications + profile fields. */
  canManageTeacher: boolean;
  /** Assign to a group via the «Заявка» flow. */
  canAddApplication: boolean;
  /** Edit the profile fields (rating / note / courses). */
  canEditProfile: boolean;
  /** Write an email to the teacher. */
  canContactEmail: boolean;
  /** Link / unlink PandaDoc contracts. */
  canManagePandadoc: boolean;
  /**
   * Read the survey-derived feedback report (spec 0027). A DIFFERENT gate from
   * `canManageTeacher`: the backend's TEACHER_INSIGHT_ROLES is the survey
   * managers + HR, so Methodist is in and SalesManager is out. The tab must be
   * hidden for the rest — a denied query REJECTS the Apollo promise and would
   * surface as a red toast (the ContractTab/PandaDoc lesson).
   */
  canReadSurveyFeedback: boolean;
  /**
   * Read the hiring dossier (spec 0028): interviews, AI competency profile,
   * candidate documents — and the forward-load panels, which sit behind the same
   * backend gate.
   *
   * The same role set as `canManageTeacher`, but a SEPARATE flag: it mirrors a
   * different backend constant (`TEACHER_CARD_ROLES` / `INTERVIEW_ROLES`), and
   * conflating them would silently drift the day either side changes.
   *
   * FinanceManager reaches `/teachers/:id/details` but is NOT in that set, so
   * without this flag the load query fired on every card open and the «Найм» tab
   * offered three queries the backend denies — and a denied query REJECTS the
   * Apollo promise (the ContractTab/PandaDoc lesson).
   */
  canReadTeacherDossier: boolean;
}

// Mirror the BACKEND role gates exactly — a mismatch shows a control the
// backend then denies (silent failure / misleading empty state).
// Teacher schema (query + mutations incl. setTeacherProfile / applications):
const TEACHER_ROLES = [
  UserRole.ADMIN,
  UserRole.ACCOUNTANT, // AccountManager
  UserRole.SALESMANAGER,
  UserRole.HRMANAGER,
];
// PandaDoc schema (pandadocDocuments + link/unlink) — NB: HRManager is NOT
// allowed here, but FinanceManager is.
const PANDADOC_ROLES = [
  UserRole.ADMIN,
  UserRole.ACCOUNTANT,
  UserRole.SALESMANAGER,
  UserRole.FINANCIER, // FinanceManager
];
// surveys.schema.TEACHER_INSIGHT_ROLES = SURVEY_MANAGER_ROLES + HRManager.
// NB: Methodist IS allowed and SalesManager is NOT — the exact inverse of
// PANDADOC_ROLES on both counts.
const SURVEY_INSIGHT_ROLES = [
  UserRole.ADMIN,
  UserRole.ACCOUNTANT, // AccountManager
  UserRole.METHODIST,
  UserRole.HRMANAGER,
];

const hasAnyRole = (roles: UserRole[], allowed: UserRole[]): boolean =>
  roles.some((role) => allowed.includes(role));

export const useTeacherPermissions = (): ITeacherPermissions => {
  const userState = useReactiveVar(userStateVar);
  const roles = (userState.me?.roles ?? []) as UserRole[];

  const canManageTeacher = hasAnyRole(roles, TEACHER_ROLES);

  return {
    canManageTeacher,
    canAddApplication: canManageTeacher,
    canEditProfile: canManageTeacher,
    canContactEmail: canManageTeacher,
    canManagePandadoc: hasAnyRole(roles, PANDADOC_ROLES),
    canReadSurveyFeedback: hasAnyRole(roles, SURVEY_INSIGHT_ROLES),
    // teachers_query.TEACHER_CARD_ROLES == teachers.schema INTERVIEW_ROLES ==
    // TEACHER_ROLES here. FinanceManager is deliberately absent from all three.
    canReadTeacherDossier: hasAnyRole(roles, TEACHER_ROLES),
  };
};
